import { isAxiosError } from 'axios'

/**
 * Response codes the CAPTCHA-protected v6 auth routes can return.
 *
 * `captcha_token_invalid` and `captcha_unavailable` come from VerifyRecaptcha::MODE_STRICT, which
 * covers `auth/forgot` and `validate/email` only. The legacy routes (login, sign-up) never emit
 * them, so handling here is additive.
 */
export const CAPTCHA_RESPONSE_CODES = {
  visualChallengeNeeded: 'visual_challenge_needed',
  tokenInvalid: 'captcha_token_invalid',
  unavailable: 'captcha_unavailable',
} as const

export type ICaptchaResponseCode =
  (typeof CAPTCHA_RESPONSE_CODES)[keyof typeof CAPTCHA_RESPONSE_CODES]

const THROTTLED_STATUS = 429

const CAPTCHA_ERROR_MESSAGES: Record<ICaptchaResponseCode, string> = {
  [CAPTCHA_RESPONSE_CODES.visualChallengeNeeded]: 'Please complete the verification to continue.',
  [CAPTCHA_RESPONSE_CODES.tokenInvalid]: "We couldn't verify the security check. Please try again.",
  [CAPTCHA_RESPONSE_CODES.unavailable]:
    'The security check is temporarily unavailable. Please try again in a few minutes.',
}

export const CAPTCHA_THROTTLED_MESSAGE =
  'Too many attempts. Please wait a few minutes and try again.'

/**
 * The code arrives as the response `message`, which normalizeApiError flattens under that key.
 * The stores read `email` / `error`, so an unmapped code resolves to an empty string and the form
 * renders nothing at all.
 */
export const getCaptchaResponseCode = (e: unknown): string =>
  isAxiosError(e) ? e.errors?.getError('message') || '' : ''

export const isCaptchaResponseCode = (e: unknown, code: ICaptchaResponseCode): boolean =>
  getCaptchaResponseCode(e) === code

/**
 * True for the `auth-recovery` / `email-validation` named limiters, which exist only on the strict
 * routes and answer with a status rather than a code.
 *
 * Scoped to captcha-protected v6 requests deliberately. A bare status check would also claim
 * `passwords.throttled` on forgot password, which has its own one-minute copy, and any 429 from
 * the sign-up call that shares a catch block with `validateEmail`.
 */
export const isCaptchaThrottled = (e: unknown): boolean =>
  isAxiosError(e) &&
  e.response?.status === THROTTLED_STATUS &&
  Boolean(e.config?.reCaptchaStore) &&
  e.config?.apiVersion === 'v6'

/**
 * User-facing copy for a captcha failure, or null when the error is not captcha-related — the
 * caller then falls back to its own field-level handling.
 */
export const getCaptchaErrorMessage = (e: unknown): string | null => {
  if (isCaptchaThrottled(e)) return CAPTCHA_THROTTLED_MESSAGE

  const code = getCaptchaResponseCode(e)

  return code in CAPTCHA_ERROR_MESSAGES
    ? CAPTCHA_ERROR_MESSAGES[code as ICaptchaResponseCode]
    : null
}
